"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.orderCancellationStateSchema = exports.orderPendingCancelRequestSchema = exports.orderCancellationRecordSchema = exports.orderCancellationTicketOutcomeSchema = exports.CANCEL_ACKNOWLEDGMENT_STALE_MESSAGE = exports.CANCEL_ACKNOWLEDGMENT_REQUIRED_MESSAGE = exports.orderCancelAcknowledgmentSchema = exports.orderCancelAcknowledgmentPaymentSchema = exports.orderCancelAcknowledgmentInvoiceSchema = exports.PAYMENT_SCHEDULED_STATUS = exports.INVOICE_NON_LIVE_STATUSES = exports.orderCancelRequestResponseSchema = exports.orderCancelRequestSchema = exports.CANCEL_REASON_MAX = exports.orderCancelTicketChoiceInputSchema = exports.orderCancelEligibilityResponseSchema = exports.CANCEL_REQUEST_CHANGES_REFUSED_MESSAGE = exports.CANCEL_NO_APPROVAL_RULE_MESSAGE = exports.CANCEL_BL_APPEARED_MESSAGE = exports.CANCEL_INELIGIBILITY_MESSAGES = exports.cancelIneligibilityCodeSchema = exports.CANCEL_INELIGIBILITY_CODES = exports.orderCancelTicketChoiceSchema = exports.ORDER_CANCEL_TICKET_CHOICES = void 0;
exports.orderCancelledWriteRefusalMessage = orderCancelledWriteRefusalMessage;
exports.isLiveInvoiceStatus = isLiveInvoiceStatus;
const zod_1 = require("zod");
const approval_rules_1 = require("./approval-rules");
// The per-ticket outcome the submitter picks at request time. Mirrors the
// Prisma `order_cancel_ticket_choice` enum. `keep` is the frontend's default;
// the backend never defaults a choice — a submit must name one per ticket.
exports.ORDER_CANCEL_TICKET_CHOICES = ['keep', 'delete'];
exports.orderCancelTicketChoiceSchema = zod_1.z.enum(exports.ORDER_CANCEL_TICKET_CHOICES);
// One code per eligibility check. The codes are stable wire values; the
// messages below are the copy the product owns, and both the eligibility read
// and the submit refusal serve the same pair so the two can never diverge.
exports.CANCEL_INELIGIBILITY_CODES = [
    'not_b2b',
    'no_ticket_link',
    'multi_ticket',
    'live_bl',
    'declared_to_lender',
    'already_cancelled',
    'request_pending',
    'remainder_unaccounted',
];
exports.cancelIneligibilityCodeSchema = zod_1.z.enum(exports.CANCEL_INELIGIBILITY_CODES);
// Product-owned copy, one message per code. The frontend renders these; the
// backend refuses with them. The string itself is the contract — never reword
// one to suit a call site.
exports.CANCEL_INELIGIBILITY_MESSAGES = {
    not_b2b: 'Only matched buy and sell orders can be cancelled.',
    no_ticket_link: 'No tickets are linked to this order.',
    multi_ticket: 'This order was matched from several tickets. Cancellation is not available for it yet.',
    live_bl: 'This order has a BL. Remove the BL first. A BL that cannot be removed means the order is past pre-shipment.',
    declared_to_lender: 'This order was declared to a lender. Cancellation is not available for an order that has shipped.',
    already_cancelled: 'This order is already cancelled.',
    request_pending: 'A cancel request is already pending on this order.',
    // Signed off by the product owner on 2026-09-11, who asked for the fuller
    // wording: state the condition, its consequence, and that it is temporary.
    // Every other string in this map is wording the product owner signed off up
    // front; this one is the exception, because the condition was decided later.
    //
    // What it refuses: an order whose match recorded a leftover quantity that is
    // either not recorded at all or above zero. An uneven match can carve the
    // leftover into its own ticket, the match never reduces the original ticket's
    // quantity, and nothing ties a historical clone back to its source — so
    // releasing the ticket would offer the same material twice. Only a recorded
    // zero, with no ticket naming either of the order's tickets through the
    // remainder link, is safe to release.
    remainder_unaccounted: "Part of this order's ticket quantity cannot be accounted for, so releasing its tickets could offer the same material twice. Cancellation is not available for it yet.",
};
// Raised by the outcome's re-check when a BL appeared while the request was
// pending. Deliberately its own string: the approver is told what changed
// under them, not why the order was never eligible.
exports.CANCEL_BL_APPEARED_MESSAGE = 'This order now has a BL and cannot be cancelled.';
// The submit refusal when no enabled approval rule covers cancellation. This
// path never auto-approves, unlike the ticket and hedge submit paths.
exports.CANCEL_NO_APPROVAL_RULE_MESSAGE = 'No approval rule covers cancellation. Ask an admin to enable it.';
// The act-on refusal for `request_changes` on a cancel request. A submitter
// cannot edit a pending request, so a changes-requested state is a dead end.
exports.CANCEL_REQUEST_CHANGES_REFUSED_MESSAGE = 'Request changes is not available on a cancel request. Approve or reject it.';
// The one message every refused write to a Cancelled order answers with.
function orderCancelledWriteRefusalMessage(orderId) {
    return `Order ${orderId} is cancelled and cannot be changed.`;
}
exports.orderCancelEligibilityResponseSchema = zod_1.z.object({
    eligible: zod_1.z.boolean(),
    code: exports.cancelIneligibilityCodeSchema.nullable(),
    message: zod_1.z.string().nullable(),
});
exports.orderCancelTicketChoiceInputSchema = zod_1.z
    .object({
    ticket_id: zod_1.z.string().regex(/^\d+$/, 'ticket_id must be a positive integer id'),
    choice: exports.orderCancelTicketChoiceSchema,
})
    .strict();
exports.CANCEL_REASON_MAX = 2000;
exports.orderCancelRequestSchema = zod_1.z
    .object({
    // Mandatory and non-blank: the trim happens before the length check, so a
    // whitespace-only reason is refused at the schema, not in the service.
    reason: zod_1.z.string().trim().min(1, 'A reason is required').max(exports.CANCEL_REASON_MAX),
    // Exactly one choice per ticket linked to the order. The set is validated
    // against the order's own ticket-link row on submit, not trusted as given.
    ticket_choices: zod_1.z.array(exports.orderCancelTicketChoiceInputSchema).min(1).max(2),
})
    .strict();
exports.orderCancelRequestResponseSchema = zod_1.z.object({
    request_id: zod_1.z.string().uuid(),
    workflow_id: zod_1.z.string().uuid(),
    order_id: zod_1.z.string(),
    reason: zod_1.z.string(),
    submitted_by_user_id: zod_1.z.string().uuid().nullable(),
    submitted_at: zod_1.z.string(),
    ticket_choices: zod_1.z.array(zod_1.z.object({
        ticket_id: zod_1.z.string(),
        choice: exports.orderCancelTicketChoiceSchema,
    })),
});
// ---------------------------------------------------------------------------
// The invoice / payment acknowledgment
// ---------------------------------------------------------------------------
// The status words the code treats as live. Stated by exclusion, exactly as the
// invoicing module's chain-value reader states it: every other word is live, the
// two legacy synonyms (`Partial` for `Partially Paid`, `Sent` for `Issued`) and
// `Pending` included, because none of the three is in this set. A row with no
// status word at all is not live either — the same reader requires a word.
exports.INVOICE_NON_LIVE_STATUSES = ['Draft', 'Void', 'Cancelled'];
// The payment status that marks a plan rather than a settled payment. The
// invoicing module's own paid-status derivation draws the line here, and this
// read draws it in the same place. Two writers store plans this way: the
// tranches of a final-settlement invoice and the marker written when a purchase
// invoice is marked raised.
exports.PAYMENT_SCHEDULED_STATUS = 'Scheduled';
function isLiveInvoiceStatus(status) {
    if (status === null)
        return false;
    return !exports.INVOICE_NON_LIVE_STATUSES.some((word) => word === status);
}
exports.orderCancelAcknowledgmentInvoiceSchema = zod_1.z.object({
    invoice_id: zod_1.z.string(),
    invoice_number: zod_1.z.string().nullable(),
    // `Purchase` or `Sale`; null on a row the invoicing module never sided.
    invoice_direction: zod_1.z.string().nullable(),
    company_name: zod_1.z.string().nullable(),
    status: zod_1.z.string().nullable(),
});
exports.orderCancelAcknowledgmentPaymentSchema = zod_1.z.object({
    payment_id: zod_1.z.string(),
    invoice_id: zod_1.z.string(),
    invoice_number: zod_1.z.string().nullable(),
    // Decimal as a string so no precision is lost crossing the wire. Never summed:
    // one order's payments can be in two or three currencies, so a single total
    // has no meaning and none is served.
    amount: zod_1.z.string(),
    // A blank currency or a blank paid date is rendered as "unknown" rather than
    // hidden — money is never dropped from the list because a field is blank.
    currency: zod_1.z.string().nullable(),
    paid_date: zod_1.z.string().nullable(),
    // The payment itself is live; its host invoice is soft-deleted. The money was
    // received, so the row is shown and flagged instead of dropped.
    invoice_deleted: zod_1.z.boolean(),
});
exports.orderCancelAcknowledgmentSchema = zod_1.z.object({
    order_id: zod_1.z.string(),
    invoices: zod_1.z.array(exports.orderCancelAcknowledgmentInvoiceSchema),
    payments: zod_1.z.array(exports.orderCancelAcknowledgmentPaymentSchema),
});
// The approve refusal when the list is non-empty and no acknowledgment came
// with the call. The UI keeps Approve disabled for the same reason, so a direct
// call cannot reach past what the surface refuses to offer.
exports.CANCEL_ACKNOWLEDGMENT_REQUIRED_MESSAGE = 'Acknowledge the invoices and payments on this order before approving.';
// The approve refusal when the id set moved between the list being rendered and
// the approve landing: an invoice entered a live status, a settled payment was
// added, or a listed payment was deleted.
exports.CANCEL_ACKNOWLEDGMENT_STALE_MESSAGE = 'The invoices on this order changed. Review them again.';
// ---------------------------------------------------------------------------
// The cancellation record the order detail response serves
// ---------------------------------------------------------------------------
exports.orderCancellationTicketOutcomeSchema = zod_1.z.object({
    ticketId: zod_1.z.string(),
    choice: exports.orderCancelTicketChoiceSchema,
});
// What the cancel wrote onto the order, served back so the cancelled page can
// say what happened and who decided it. `cancelledAt` is non-null by
// construction: the record exists only when the order carries a cancel time,
// which is the same presence test the money projections use, so the record and
// the frozen figures can never disagree about whether this order was cancelled
// through this flow. A legacy cancelled order carries no cancel time and
// therefore no record, which is correct — its money was never frozen either.
exports.orderCancellationRecordSchema = zod_1.z.object({
    cancelledAt: zod_1.z.string(),
    reason: zod_1.z.string().nullable(),
    // Display names, resolved from the linked users. Null when the user row is
    // gone or carries no name — the record still stands without them.
    requestedByName: zod_1.z.string().nullable(),
    approvedByName: zod_1.z.string().nullable(),
    // The role the approver actually played, read off their approval action
    // rather than assumed from the seeded rule: an admin may edit the cancel
    // rule, so the role that signed a given cancel is a fact about that cancel.
    // Null when the action row cannot be matched to the recorded approver.
    approvedByRole: approval_rules_1.pgApproverRoleSchema.nullable(),
    // One entry per ticket the request named, so the page can say what became of
    // each. Empty when the approved request's choices are no longer readable.
    ticketOutcomes: zod_1.z.array(exports.orderCancellationTicketOutcomeSchema),
});
// A cancel request waiting on an approver. Present while the request's workflow
// is still active — the same active-workflow pair the eligibility check reads
// before it answers `request_pending`, so the order page's notice and the entry
// point's disabled note cannot disagree.
exports.orderPendingCancelRequestSchema = zod_1.z.object({
    submittedAt: zod_1.z.string(),
    reason: zod_1.z.string(),
    submittedByName: zod_1.z.string().nullable(),
});
// The whole cancellation block on the order detail response. Null when the
// deal-cancellation flag is off: the flow is dark, so nothing about it is
// served and nothing about it renders.
exports.orderCancellationStateSchema = zod_1.z.object({
    record: exports.orderCancellationRecordSchema.nullable(),
    pendingRequest: exports.orderPendingCancelRequestSchema.nullable(),
});
//# sourceMappingURL=order-cancellation.js.map